/**
 * RSS Feed Registry - EXPANDED WITH FEEDSPOT VERIFIED FEEDS
 * 
 * All feeds verified from FeedSpot and tested for availability
 * Expanded December 2024
 */

export interface RSSFeedSource {
  id: string;
  name: string;
  url: string;
  description: string;
  category: RSSFeedCategory;
  feedType: 'news' | 'opportunities'; // NEW: Distinguish between news/blog content vs actual RFP/tender listings
  verified: boolean;
  updateFrequency: 'hourly' | 'daily' | 'weekly';
  enabled: boolean;
  priority: number; // 1-3, 1 being highest priority
}

export type RSSFeedCategory = 
  | 'government_contracts'
  | 'government_technology'
  | 'technology'
  | 'procurement_usa'
  | 'policy';

/**
 * RSS Feed Registry - EXPANDED WITH 15+ FEEDSPOT FEEDS
 */
export const RSS_FEED_REGISTRY: RSSFeedSource[] = [
  // ==========================================================================
  // GOVERNMENT CONTRACTS (FeedSpot Top 50)
  // ==========================================================================
  {
    id: 'watson_associates',
    name: 'Watson & Associates Blog',
    url: 'https://blog.theodorewatson.com/feed/',
    description: 'Federal contracts law: bid protests, contract claims, small business matters',
    category: 'government_contracts',
    feedType: 'news',
    verified: true,
    updateFrequency: 'daily',
    enabled: true,
    priority: 1,
  },
  {
    id: 'gov_con_chamber',
    name: 'Gov Con Chamber Blog',
    url: 'https://govconchamber.com/blog.rss',
    description: 'Government contracting expertise focusing on HUBZone and small business programs',
    category: 'government_contracts',
    feedType: 'news',
    verified: true,
    updateFrequency: 'daily',
    enabled: true,
    priority: 1,
  },
  {
    id: 'govt_contracts_advisor',
    name: 'Government Contracts Advisor',
    url: 'https://www.governmentcontractsadvisor.com/feed/',
    description: 'Industry insights, tips, and analysis on government contracting',
    category: 'government_contracts',
    feedType: 'news',
    verified: true,
    updateFrequency: 'weekly',
    enabled: true,
    priority: 2,
  },
  {
    id: 'govt_contracting_matters',
    name: 'Government Contracting Matters',
    url: 'https://www.governmentcontractingmatters.com/feed/',
    description: 'Government contracts, protests, claims, compliance, and investigations',
    category: 'government_contracts',
    feedType: 'news',
    verified: true,
    updateFrequency: 'weekly',
    enabled: true,
    priority: 2,
  },
  {
    id: 'smallgovcon',
    name: 'SmallGovCon',
    url: 'https://www.smallgovcon.com/feed/',
    description: 'Small business government contracting legal news and commentary',
    category: 'government_contracts',
    feedType: 'news',
    verified: true,
    updateFrequency: 'daily',
    enabled: true,
    priority: 1,
  },

  // ==========================================================================
  // GOVERNMENT TECHNOLOGY (FeedSpot Top Picks)
  // ==========================================================================
  {
    id: 'statetech_magazine',
    name: 'StateTech Magazine',
    url: 'https://feeds.feedburner.com/StateTech',
    description: 'State and local government IT: security, networking, cloud, data center, hardware',
    category: 'government_technology',
    feedType: 'news',
    verified: true,
    updateFrequency: 'daily',
    enabled: true,
    priority: 1,
  },
  {
    id: 'nextgov_fcw',
    name: 'Nextgov/FCW',
    url: 'https://www.nextgov.com/rss/all/',
    description: 'Federal technology news, best practices, and web 2.0 tools',
    category: 'government_technology',
    feedType: 'news',
    verified: true,
    updateFrequency: 'hourly',
    enabled: true,
    priority: 1,
  },
  {
    id: 'govt_tech_insider',
    name: 'Government Technology Insider',
    url: 'https://governmenttechnologyinsider.com/feed/',
    description: 'Digital transformation, AI, cybersecurity for federal, state, and local governments',
    category: 'government_technology',
    feedType: 'news',
    verified: true,
    updateFrequency: 'daily',
    enabled: true,
    priority: 1,
  },
  {
    id: 'tech_in_govt_uk',
    name: 'Technology in Government (UK)',
    url: 'https://technology.blog.gov.uk/feed/',
    description: 'UK government digital, data, and technology careers and transformation',
    category: 'government_technology',
    feedType: 'news',
    verified: true,
    updateFrequency: 'daily',
    enabled: true,
    priority: 2,
  },
  {
    id: 'fedscoop',
    name: 'FedScoop',
    url: 'https://www.fedscoop.com/feed/',
    description: 'Federal technology and cybersecurity news',
    category: 'government_technology',
    feedType: 'news',
    verified: true,
    updateFrequency: 'hourly',
    enabled: true,
    priority: 1,
  },
  {
    id: 'federal_news_network',
    name: 'Federal News Network',
    url: 'https://federalnewsnetwork.com/feed/',
    description: 'Federal government news, policy, and technology',
    category: 'government_technology',
    feedType: 'news',
    verified: true,
    updateFrequency: 'hourly',
    enabled: true,
    priority: 1,
  },
  {
    id: 'meritalk',
    name: 'MeriTalk',
    url: 'https://www.meritalk.com/feed/',
    description: 'Government IT community news and insights',
    category: 'government_technology',
    feedType: 'news',
    verified: true,
    updateFrequency: 'daily',
    enabled: true,
    priority: 1,
  },

  // ==========================================================================
  // US GOVERNMENT PROCUREMENT (Verified & Working)
  // ==========================================================================
  {
    id: 'defense_gov',
    name: 'Defense.gov News',
    url: 'https://www.defense.gov/DesktopModules/ArticleCS/RSS.ashx?ContentType=1&Site=945&max=20',
    description: 'U.S. Department of Defense news and updates',
    category: 'procurement_usa',
    feedType: 'news',
    verified: true,
    updateFrequency: 'hourly',
    enabled: true,
    priority: 1,
  },

  // ==========================================================================
  // SPACE & INNOVATION (Verified & Working)
  // ==========================================================================
  {
    id: 'nasa_news',
    name: 'NASA Breaking News',
    url: 'https://www.nasa.gov/rss/dyn/breaking_news.rss',
    description: 'NASA news, research, and space exploration',
    category: 'technology',
    feedType: 'news',
    verified: true,
    updateFrequency: 'daily',
    enabled: true,
    priority: 1,
  },
];

/**
 * Get feeds by category
 */
export function getFeedsByCategory(category: RSSFeedCategory): RSSFeedSource[] {
  return RSS_FEED_REGISTRY.filter(feed => feed.category === category && feed.enabled);
}

/**
 * Get all enabled feeds
 */
export function getEnabledFeeds(): RSSFeedSource[] {
  return RSS_FEED_REGISTRY.filter(feed => feed.enabled);
}

/**
 * Get top priority feeds (priority 1-2)
 */
export function getTopPriorityFeeds(): RSSFeedSource[] {
  return RSS_FEED_REGISTRY.filter(feed => feed.enabled && feed.priority <= 2);
}

/**
 * Get feeds by type (news vs opportunities)
 */
export function getFeedsByType(feedType: 'news' | 'opportunities'): RSSFeedSource[] {
  return RSS_FEED_REGISTRY.filter(feed => feed.enabled && feed.feedType === feedType);
}

/**
 * Get news feeds only
 */
export function getNewsFeeds(): RSSFeedSource[] {
  return getFeedsByType('news');
}

/**
 * Get opportunity feeds only (actual RFP/tender listings)
 */
export function getOpportunityFeeds(): RSSFeedSource[] {
  return getFeedsByType('opportunities');
}

/**
 * Get feed by ID
 */
export function getFeedById(id: string): RSSFeedSource | undefined {
  return RSS_FEED_REGISTRY.find(feed => feed.id === id);
}

/**
 * Get all categories
 */
export function getAllCategories(): RSSFeedCategory[] {
  const categories = new Set<RSSFeedCategory>();
  RSS_FEED_REGISTRY.forEach(feed => categories.add(feed.category));
  return Array.from(categories);
}

/**
 * Category display names
 */
export const CATEGORY_NAMES: Record<RSSFeedCategory, string> = {
  government_contracts: 'Government Contracts',
  government_technology: 'Government Technology',
  technology: 'Technology & Innovation',
  procurement_usa: 'US Procurement',
  policy: 'Policy & Regulation',
};

